/*!
 * Copyright 2023 Quisitive Technologies, LLC. All rights reserved.
 */
import React, { useEffect } from 'react';

import { get, values } from 'lodash';
import Element from '../../../../components/element';
import Flex from '../../../../components/flex';
import Text from '../../../../components/text';
import NoData from '../../NoData';
import { create, updateData } from '../../Chart/DistributionChart/chart';
import { createArea, updateArea } from '../../Chart/AreaChart/chart';

const makeRanges = sources => {
  return sources.diastolic.ranges.map(combiner);

  function combiner(range, index) {
    return {
      interpretation: range.interpretation,
      x: {
        min: range.min,
        max: range.max
      },
      y: {
        min: sources.systolic.ranges[index].min,
        max: sources.systolic.ranges[index].max
      },
      color: range.color
    };
  }
};

const VitalSigns = ({ healthReportData }) => {
  const newObj = {
    systolic: {
      display_label: 'Systolic (SBP)',
      unit: 'mmHg',
      value: 112,
      interpretation: 'Normal',
      percentile: 0,
      ranges: [
        {
          interpretation: 'Low',
          color: '#29ADFF',
          min: 80,
          max: 99
        },
        {
          interpretation: 'Normal',
          color: '#29ADFF',
          min: 90,
          max: 119
        },
        {
          interpretation: 'Prehypertension',
          color: '#A020F0',
          min: 120,
          max: 139
        },
        {
          interpretation: 'Stage 1 Hypertension',
          color: '#FF0000',
          min: 140,
          max: 159
        },
        {
          interpretation: 'Stage 2 Hypertension',
          color: '#FF0000',
          min: 160,
          max: 200
        }
      ],
      test_name: '',
      density_plot: [],
      valid: true
    },
    diastolic: {
      display_label: 'Diastolic (DBP)',
      unit: 'mmHg',
      value: 75,
      interpretation: 'Normal',
      percentile: 0,
      ranges: [
        {
          interpretation: 'Low',
          color: '#29ADFF',
          min: 0,
          max: 59
        },
        {
          interpretation: 'Normal',
          color: '#29ADFF',
          min: 60,
          max: 79
        },
        {
          interpretation: 'Prehypertension',
          color: '#A020F0',
          min: 80,
          max: 89
        },
        {
          interpretation: 'Stage 1 Hypertension',
          color: '#FF0000',
          min: 90,
          max: 99
        },
        {
          interpretation: 'Stage 2 Hypertension',
          color: '#FF0000',
          min: 100,
          max: 130
        }
      ],
      test_name: '',
      density_plot: [],
      valid: true
    }
  };
  useEffect(() => {
    const newBalanceList = values(healthReportData.vital_signs);
    const newArray = [];
    newBalanceList.filter(each => {
      let keyId = '';
      if (each.display_label === 'Heart Rate') {
        keyId = 'heart_rate';
      } else if (each.display_label === 'Oxygen Saturation') {
        keyId = 'oxygen_saturation';
      } else if (each.display_label === 'Temperature') {
        keyId = 'temperature';
      } else {
        keyId = '';
      }
      const data = {
        ...each,
        section: 'vital_sign',
        chartVal: each.value,
        key: keyId
      };
      if (
        each.display_label === 'Heart Rate' ||
        each.display_label === 'Oxygen Saturation' ||
        each.display_label === 'Temperature'
      ) {
        newArray.push(data);
      }
    });

    newArray.map(each => {
      const settingsDist = {
        el:
          each.display_label.replace(/ /g, '_').replace(/[^a-zA-Z ]/g, '') ||
          'none',
        margin: {
          top: 0,
          right: 0,
          bottom: 0,
          left: 0
        }
      };
      create(settingsDist);
      updateData(each);
    });

    const data = {
      x: get(healthReportData, 'vital_signs.blood_pressure.diastolic.value'),
      y: get(healthReportData, 'vital_signs.blood_pressure.systolic.value'),
      color: get(healthReportData, 'vital_signs.blood_pressure.diastolic.color')
    };

    const settings = {
      el: 'blood_pressure_chart',
      margin: { top: 0, right: 0, bottom: 0, left: 0 },
      // TODO: read ranges from data, add colors here.
      ranges: makeRanges(newObj)
    };

    createArea(settings);
    updateArea(data);
  }, []);
  return (
    <Element>
      {get(healthReportData, 'vital_signs.blood_pressure.diastolic.value') ===
        null &&
      get(healthReportData, 'vital_signs.blood_pressure.systolic.value') ===
        null ? (
        <NoData />
      ) : (
        <Element>
          <Element>
            <Flex>
              <Element width="46.58%">
                <Text family="GTAM" size="21px" color="#222222">
                  Blood Pressure
                </Text>
                <Flex>
                  <Element marginRight="65px">
                    <Text
                      family="GTAR"
                      size="16px"
                      color="#959595"
                      marginBottom="0"
                      marginTop="0"
                    >
                      Systolic (SBP)
                    </Text>
                    <Text
                      family="GTAM"
                      size="32px"
                      color="#222222"
                      marginTop="0"
                      marginLeft="15px"
                    >
                      {get(
                        healthReportData,
                        'vital_signs.blood_pressure.systolic.value'
                      )}
                    </Text>
                  </Element>
                  <Element>
                    <Text
                      family="GTAR"
                      size="16px"
                      color="#959595"
                      marginBottom="0"
                      marginTop="0"
                    >
                      Diastolic (DBP)
                    </Text>
                    <Text
                      family="GTAM"
                      size="32px"
                      color="#222222"
                      marginTop="0"
                      marginLeft="15px"
                    >
                      {get(
                        healthReportData,
                        'vital_signs.blood_pressure.diastolic.value'
                      )}
                    </Text>
                  </Element>
                </Flex>
              </Element>
              <Element>
                <Text family="GTAM" size="21px" color="#222222">
                  Height
                </Text>
                <Flex>
                  <Element marginRight="65px">
                    <Text
                      family="GTAR"
                      size="16px"
                      color="#959595"
                      marginBottom="0"
                      marginTop="0"
                    >
                      Feet
                    </Text>
                    <Text
                      family="GTAM"
                      size="32px"
                      color="#222222"
                      marginTop="0"
                    >
                      {get(healthReportData, 'vital_signs.height.feet.value') ||
                        0}
                      ’
                    </Text>
                  </Element>
                  <Element>
                    <Text
                      family="GTAR"
                      size="16px"
                      color="#959595"
                      marginBottom="0"
                      marginTop="0"
                    >
                      Inches
                    </Text>
                    <Text
                      family="GTAM"
                      size="32px"
                      color="#222222"
                      marginTop="0"
                    >
                      {get(
                        healthReportData,
                        'vital_signs.height.inches.value'
                      ) || 0}
                      ”
                    </Text>
                  </Element>
                </Flex>
              </Element>
            </Flex>
            <Flex>
              <Element width="37%" margin="60px 5px 0px 80px">
                <div id="blood_pressure_chart"></div>
              </Element>
              <Element>
                <Flex align="center" marginTop="41px">
                  <Element width="450px">
                    <Text family="GTAM" size="21px" color="#222222">
                      Heart Rate
                    </Text>

                    <Text
                      family="GTAM"
                      size="32px"
                      color="#222222"
                      marginTop="0"
                      marginBottom="0"
                    >
                      {get(healthReportData, 'vital_signs.heart_rate.value')}
                    </Text>
                    <Text
                      family="GTAR"
                      size="16px"
                      color="#959595"
                      marginTop="0"
                    >
                      beats/minute
                    </Text>
                  </Element>
                  <div
                    id={get(
                      healthReportData,
                      'vital_signs.heart_rate.display_label'
                    )
                      .replace(/ /g, '_')
                      .replace(/[^a-zA-Z ]/g, '')}
                  ></div>
                </Flex>
                <Flex align="center" marginTop="30px">
                  <Element width="450px">
                    <Text family="GTAM" size="21px" color="#222222">
                      Oxygen Saturation
                    </Text>

                    <Text
                      family="GTAM"
                      size="32px"
                      color="#222222"
                      marginTop="0"
                      marginBottom="0"
                    >
                      {get(
                        healthReportData,
                        'vital_signs.oxygen_saturation.value'
                      )}
                      %
                    </Text>
                    <Text
                      family="GTAR"
                      size="16px"
                      color="#959595"
                      marginTop="0"
                    >
                      SO2%
                    </Text>
                  </Element>
                  <div
                    id={get(
                      healthReportData,
                      'vital_signs.oxygen_saturation.display_label'
                    )
                      .replace(/ /g, '_')
                      .replace(/[^a-zA-Z ]/g, '')}
                  ></div>
                </Flex>
                <Flex align="center" marginTop="30px">
                  <Element width="450px">
                    <Text
                      family="GTAM"
                      size="21px"
                      color="#222222"
                      // marginBottom="0"
                      // marginTop="0"
                    >
                      Temperature
                    </Text>

                    <Text
                      family="GTAM"
                      size="32px"
                      color="#222222"
                      marginTop="0"
                      marginBottom="0"
                    >
                      {get(healthReportData, 'vital_signs.temperature.value') ||
                        0}
                    </Text>
                    <Text
                      family="GTAR"
                      size="16px"
                      color="#959595"
                      marginTop="0"
                    >
                      °F
                    </Text>
                  </Element>
                  <div
                    id={get(
                      healthReportData,
                      'vital_signs.temperature.display_label'
                    )
                      .replace(/ /g, '_')
                      .replace(/[^a-zA-Z ]/g, '')}
                  ></div>
                </Flex>
              </Element>
            </Flex>
          </Element>
        </Element>
      )}
    </Element>
  );
};

export default VitalSigns;
